import { useState } from "react";
import { toast } from "sonner";
import { Smartphone, Share, PlusSquare, X, Download, MoreVertical } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { useInstall, isPhone } from "../lib/install";

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3 text-sm"><span className="h-6 w-6 shrink-0 rounded-full bg-[#00E599]/15 text-[#00E599] grid place-items-center text-xs font-bold">{n}</span>
    <span className="pt-0.5">{children}{Icon && <Icon size={14} className="inline ml-1.5 text-[#00E599]" />}</span></li>);

export const InstallDialog = ({ onClose }) => {
  const app = useInstall();
  const go = async () => { if (await app.prompt()) { toast.success("Comply369 is on your home screen"); onClose(); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#121824] border-[#1F2A3E] max-w-sm" data-testid="install-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg flex items-center gap-2"><Smartphone size={18} className="text-[#00E599]" />Install Comply369</DialogTitle>
          <p className="text-xs text-slate-400">Opens from your home screen like an app, full screen, and works at sites with no signal.</p></DialogHeader>
        {app.installed ? <p className="text-sm text-emerald-300" data-testid="install-done">Already installed on this device.</p>
          : app.canPrompt ? <Button onClick={go} className="bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold" data-testid="install-now-btn"><Download size={15} className="mr-1.5" />Install now</Button>
            : app.ios ? <ol className="grid gap-3" data-testid="install-ios-steps">
              <Step n={1}>Open this page in Safari</Step>
              <Step n={2} icon={Share}>Tap the Share button</Step>
              <Step n={3} icon={PlusSquare}>Choose "Add to Home Screen", then tap Add</Step></ol>
              : <ol className="grid gap-3" data-testid="install-other-steps">
                <Step n={1} icon={MoreVertical}>Open the browser menu</Step>
                <Step n={2}>Choose "Install app" or "Add to Home screen"</Step>
                <Step n={3}>Confirm. The Comply369 icon appears on your home screen</Step></ol>}
      </DialogContent>
    </Dialog>);
};

const DISMISS = "c369-install-dismissed";

export const InstallBanner = () => {
  const app = useInstall();
  const [hidden, setHidden] = useState(() => !!localStorage.getItem(DISMISS));
  const [open, setOpen] = useState(false);
  if (hidden || app.installed || !isPhone()) return null;
  return (
    <div className="c-surface c-rise mb-6 flex items-center gap-3 p-4 !border-[#00E599]/30" data-testid="install-banner">
      <Smartphone size={20} className="text-[#00E599] shrink-0" />
      <div className="flex-1 min-w-0"><div className="text-sm font-semibold">Install Comply369 on this phone</div>
        <div className="text-xs text-slate-400">Opens like an app and works at sites with no signal.</div></div>
      <Button size="sm" onClick={() => setOpen(true)} className="bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold" data-testid="install-banner-btn">Install</Button>
      <button aria-label="Dismiss" onClick={() => { localStorage.setItem(DISMISS, "1"); setHidden(true); }} className="p-1 text-slate-500 hover:text-white" data-testid="install-banner-dismiss"><X size={15} /></button>
      {open && <InstallDialog onClose={() => setOpen(false)} />}
    </div>);
};
